import { useEffect } from "react";
import { Archive, RotateCcw, Trash2, X } from "lucide-react";
import {
  useArchivedPanneaux,
  useDeletePanneauPermanently,
  useRestorePanneau,
} from "../api/queries";

interface Props {
  conv: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Modal listing soft-archived panneaux for the current conversation,
 * newest-archived first. Each row has Restaurer (un-archive) and
 * Supprimer définitivement (hard delete) actions.
 *
 * UX rationale ("Don't Make Me Think")
 * -----------------------------------
 * The single-click panneau close (Minus button on each panneau
 * header) is intentionally low-friction. This modal is the safety
 * net: it makes archive *visible*, *findable*, and *reversible*.
 * Hard delete is a separate explicit Trash button — never the
 * default action — and the backend additionally guards against
 * deleting active panneaux.
 *
 * Modal vs page: a modal stays in-context (operator was looking at
 * panneaux, opens archived list, restores one, returns to where
 * they were). A separate /archives page would force a navigation
 * round-trip that isn't worth its complexity at v1 scale.
 */
export function ArchivedPanneauxModal({ conv, open, onClose }: Props) {
  const archived = useArchivedPanneaux(conv);
  const restore = useRestorePanneau(conv);
  const hardDelete = useDeletePanneauPermanently(conv);

  // Escape closes — standard modal expectation.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const rows = archived.data ?? [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Panneaux archivés"
      className="fixed inset-0 z-30 flex items-center justify-center px-4"
    >
      {/* Backdrop. Click closes — same pattern as ExpandedPanneauModal. */}
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
      />

      <div className="relative w-full max-w-2xl rounded-2xl bg-white dark:bg-surface-dark shadow-xl ring-1 ring-black/10 dark:ring-white/10 max-h-[80vh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-black/10 dark:border-white/10">
          <div className="flex items-center gap-2">
            <Archive className="h-4 w-4 text-chip-orange" />
            <h2 className="text-base font-semibold text-ink dark:text-ink-dark">
              Panneaux archivés
            </h2>
            {rows.length > 0 && (
              <span className="text-xs text-muted dark:text-muted-dark">
                ({rows.length})
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="p-1 rounded text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark hover:bg-canvas dark:hover:bg-canvas-dark/40"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="overflow-y-auto flex-1 px-3 py-2">
          {archived.isLoading && (
            <div className="text-center text-sm text-muted dark:text-muted-dark py-6">
              Chargement…
            </div>
          )}
          {archived.isSuccess && rows.length === 0 && (
            <div className="text-center text-sm text-muted dark:text-muted-dark py-10">
              Aucun panneau archivé.
            </div>
          )}
          <ul className="divide-y divide-black/5 dark:divide-white/10">
            {rows.map((p) => {
              const archivedAt = p.archived_at
                ? new Date(p.archived_at).toLocaleString("fr-FR", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })
                : "";
              const busy =
                (restore.isPending && restore.variables === p.id) ||
                (hardDelete.isPending && hardDelete.variables === p.id);
              return (
                <li
                  key={p.id}
                  className="flex items-center gap-3 py-2.5 px-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink dark:text-ink-dark truncate">
                      {p.name}
                    </div>
                    <div className="text-xs text-muted dark:text-muted-dark">
                      <span className="capitalize">
                        {p.kind.replace("_", " ")}
                      </span>
                      {archivedAt && <> · fermé le {archivedAt}</>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => restore.mutate(p.id)}
                    disabled={busy}
                    title="Restaurer ce panneau"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs bg-white dark:bg-surface-dark ring-1 ring-black/10 dark:ring-white/10 hover:ring-ink/40 dark:hover:ring-white/40 text-ink dark:text-ink-dark disabled:opacity-50"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Restaurer
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Supprimer définitivement « ${p.name} » ? Cette action est irréversible.`,
                        )
                      ) {
                        hardDelete.mutate(p.id);
                      }
                    }}
                    disabled={busy}
                    title="Supprimer définitivement"
                    aria-label="Supprimer définitivement"
                    className="inline-flex items-center justify-center h-7 w-7 rounded-full text-muted dark:text-muted-dark hover:text-chip-red hover:bg-canvas dark:hover:bg-canvas-dark/40 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <footer className="px-5 py-2 text-xs text-muted dark:text-muted-dark border-t border-black/5 dark:border-white/10">
          Les panneaux fermés restent ici jusqu'à suppression manuelle.
        </footer>
      </div>
    </div>
  );
}
